'use client'

import { useGovernance } from '@hooks/useGovernance'
import { Box, styled } from '@mui/material'
import React, { memo, useCallback, useEffect, useMemo } from 'react'
import { Text } from '@components/Helpers'
import { useTranslation } from 'react-i18next'
import { usePathname } from 'next/navigation'
import DewiButton from '@components/DewiButton'
import useSWR from 'swr'
import { useSolana } from '@dewicats/connect-button'
import { ProposalDraft } from '@prisma/client'
import { DewiRadius2xl, DewiSpacing2 } from '@furry/tokens'
import { useAsync } from 'react-async-hook'
import { getDeWiCatCollectables, solAddressIsValid } from '@utils/solanaUtils'
import { useProposal } from '@helium/modular-governance-hooks'
import { PublicKey } from '@solana/web3.js'
import { useVotes } from '@hooks/useVotes'
import dynamic from 'next/dynamic'

const ConnectButton = dynamic(() => import('@dewicats/connect-button'), {
  ssr: false,
})

const BoldText = {
  fontWeight: 'bold',
}

const SubmitVoteGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  width: '100%',
}))

const PrimaryButton = styled(DewiButton)({
  textTransform: 'none',
  backgroundColor: 'white',
  borderRadius: DewiRadius2xl,
})

const PublishButton = styled(DewiButton)({
  textTransform: 'none',
  backgroundColor: 'white',
  color: 'black',
  borderRadius: DewiRadius2xl,
  marginRight: DewiSpacing2,
})

const SaveDraftButtonText = { fontWeight: 'bold' }

const PublishButtonText = {
  fontWeight: 'bold',
  color: 'var(--dewi_colors_dark_9) !important',
}

const ProposalNavBarActions = () => {
  const { t } = useTranslation()
  const pathname = usePathname()
  const { anchorProvider } = useSolana()
  const {
    setConfirmDraftProposal,
    setVoteDialog,
    setConfirmReviewProposal,
    setPublishProposal,
  } = useGovernance()

  const proposalKey = useMemo(() => {
    const proposalKey = pathname.split('/').pop()
    if (solAddressIsValid(proposalKey)) {
      return proposalKey
    }
    return undefined
  }, [pathname])

  const proposal = useProposal(
    proposalKey ? new PublicKey(proposalKey) : undefined,
  )

  const votes = useVotes(proposalKey ? new PublicKey(proposalKey) : undefined)

  const fetcher = (args: any) =>
    fetch(args).then((res) => res.json() as Promise<ProposalDraft[]>)

  const { data: allDraftsForReview } = useSWR(
    '/api/getDrafts?status=[{ "status": "review" }]',
    fetcher,
  )

  const { data: draftsNotForReview } = useSWR(
    `/api/getDrafts?walletAddress=${anchorProvider?.publicKey?.toBase58()}&status=[{ "status": "review" }]`,
    fetcher,
  )

  const draft = useMemo(() => {
    if (pathname.includes('/review')) {
      return allDraftsForReview?.[0]
    }

    return draftsNotForReview?.[0]
  }, [allDraftsForReview, draftsNotForReview, pathname])

  const onVote = useCallback(() => {
    setVoteDialog(true)
  }, [setVoteDialog])

  const onSaveDraft = useCallback(() => {
    setConfirmDraftProposal(true)
  }, [setConfirmDraftProposal])

  const onReview = useCallback(() => {
    setConfirmReviewProposal(true)
  }, [setConfirmReviewProposal])

  const onPublish = useCallback(() => {
    setPublishProposal(true)
  }, [setPublishProposal])

  const { result: voteButtonDisabled } = useAsync(async () => {
    if (!anchorProvider?.publicKey) return true

    if (
      votes?.markers?.find((mk: any) =>
        mk?.voter.equals(anchorProvider?.publicKey),
      )
    )
      return true

    try {
      const cats = await getDeWiCatCollectables(anchorProvider)
      return cats.length === 0
    } catch (e) {
      return true
    }
  }, [anchorProvider?.publicKey?.toBase58(), votes?.markers])

  return (
    <Box flex={1} marginRight={2}>
      <SubmitVoteGroup>
        {!pathname.includes('/create') &&
          !pathname.includes('/edit') &&
          !pathname.includes('/review') && (
            <>
              {!anchorProvider?.publicKey && <ConnectButton disableMagicLink />}
              {anchorProvider?.publicKey && (
                <PrimaryButton
                  onClick={onVote}
                  disabled={voteButtonDisabled}
                  variant="outlined"
                >
                  <Text sx={BoldText}>
                    {t('components.proposalNavBar.vote')}
                  </Text>
                </PrimaryButton>
              )}
            </>
          )}

        {(pathname.includes('/create') || pathname.includes('/edit')) &&
          (!draft || draft?.status === 'draft') && (
            <PrimaryButton variant="outlined" onClick={onSaveDraft}>
              <Text sx={SaveDraftButtonText}>
                {t('components.proposalNavBar.saveAndSubmit')}
              </Text>
            </PrimaryButton>
          )}
        {(pathname.includes('/create') || pathname.includes('/edit')) &&
          draft?.status === 'review' && (
            <PrimaryButton variant="outlined" onClick={onSaveDraft}>
              <Text sx={SaveDraftButtonText}>
                {t('components.proposalNavBar.saveAndSubmit')}
              </Text>
            </PrimaryButton>
          )}
        {/* TODO: Bring back when we add ability for team to review proposals 
        {(pathname.includes('/create') || pathname.includes('/edit')) &&
          draft?.status === 'review' && (
            <ColorBarWithText
              green={2}
              red={1}
              total={4}
              width={150}
              text={t('components.proposalNavBar.numberOfVotes', {
                start: 3,
                end: 4,
              })}
            />
          )} */}
        {pathname.includes('/review') && (
          <>
            {/* <PrimaryButton variant="outlined" onClick={onReview}>
              <Text sx={SaveDraftButtonText}>
                {t('components.proposalNavBar.review')}
              </Text>
            </PrimaryButton> */}
            {/* <PublishButton onClick={onPublish}>
              <Text sx={PublishButtonText}>
                {t('components.proposalNavBar.publish')}
              </Text>
            </PublishButton> */}
          </>
        )}
      </SubmitVoteGroup>
    </Box>
  )
}

export default memo(ProposalNavBarActions)
